const NMS_PARTICIPANTS_STORAGE_KEY = "martti.nmsParticipants"

// Which media participants this tab holds, keyed by interaction. An interpreter can be on more
// than one call at a time, so a single entry would let the second call's participant overwrite
// the first and leak it.
//
// Kept in sessionStorage because it describes one tab: a refresh keeps the same tab and should
// recover it, a new tab is genuinely a new participant. A refresh throws the running client away
// and asks for a new participant, leaving the old one open until the media server times it out
// about forty seconds later, billing twice in between (AE-1352). Remembering the old one lets us
// close it deliberately instead of waiting.

const readAll = () => {
  try {
    const stored = JSON.parse(window.sessionStorage?.getItem(NMS_PARTICIPANTS_STORAGE_KEY) || "{}")
    return stored && typeof stored === "object" ? stored : {}
  } catch (err) {
    // Storage can be unavailable (private mode, blocked cookies). Losing this costs a late
    // segment close, not correctness, so it must never take the call screen down.
    return {}
  }
}

const writeAll = (entries) => {
  try {
    window.sessionStorage?.setItem(NMS_PARTICIPANTS_STORAGE_KEY, JSON.stringify(entries))
    return true
  } catch (err) {
    return false
  }
}

// Only a genuine refresh of this tab leaves a participant of ours behind. A refresh reports
// "reload"; a duplicated or newly opened tab reports "navigate".
export const wasReloaded = () => {
  try {
    const [nav] = window.performance?.getEntriesByType?.("navigation") ?? []
    return nav?.type === "reload"
  } catch (err) {
    return false
  }
}

// Duplicating a tab copies sessionStorage, so a duplicate starts life holding the original tab's
// participants. Closing one of those would drop a session that is live and in use, so anything
// present on a fresh navigation was inherited rather than ours and is dropped. Only entries this
// tab wrote for itself survive to be acted on.
let inheritedEntriesDropped = false

const dropInheritedEntries = () => {
  if (inheritedEntriesDropped) return
  inheritedEntriesDropped = true
  if (!wasReloaded()) writeAll({})
}

// Tests only: the flag above is module state and would otherwise leak between cases.
export const _resetInheritedCheck = () => {
  inheritedEntriesDropped = false
}

export const rememberNmsParticipant = ({ interactionId, participantId }) => {
  if (!interactionId || !participantId || participantId === "NA") return false
  dropInheritedEntries()
  const entries = readAll()
  entries[interactionId] = participantId
  return writeAll(entries)
}

export const readNmsParticipant = (interactionId) => {
  if (!interactionId) return null
  dropInheritedEntries()
  return readAll()[interactionId] || null
}

export const forgetNmsParticipant = (interactionId) => {
  if (!interactionId) return writeAll({})
  const entries = readAll()
  delete entries[interactionId]
  return writeAll(entries)
}
